import { useEffect, useState } from "react";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";

import { Item, Category, MaterialPrice } from "../../../entity/Entity";
import {
    GetItemsByCategory,
    AddItem,
    DelItem,
    GetCategories,
} from "../../../api/requests/Api";

import "./AdminItems.css";
import MenuAdmin from "../menu/MenuAdmin";
import ImageUploader from "../../../utils/ImgDownloader";

const emptyItem: Item = {
    _id: "",
    title: "",
    text_description: "",
    category: "",
    tags: [],
    images_link: [],
    is_dynamic_price: false,
    is_to_order: false,
    dynamic_prices: [],
    price: 0,
    available_materials: [],
    characteristics: [],
};

const AdminItems = () => {

    const [categories, setCategories] = useState<Category[]>([]);
    const [items, setItems] = useState<Item[]>([]);
    const [selectedCategory, setSelectedCategory] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(true);
    const [message, setMessage] = useState<string | null>(null);
    const [dynamicPrices, setDynamicPrices] = useState<MaterialPrice[]>([]);
    const [currentItem, setCurrentItem] = useState<Item>(emptyItem);
    const [tagsInput, setTagsInput] = useState<string>("");

    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [
                [{ header: [1, 2, false] }],
                ["bold", "italic", "underline"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
            ],
        },
    });

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        if (selectedCategory) {
            loadItems(selectedCategory);
        } else {
            setItems([]);
            setLoading(false);
        }
    }, [selectedCategory]);

    useEffect(() => {
        if (!quill) return;

        quill.on("text-change", () => {
            setCurrentItem((prev) => ({
                ...prev,
                text_description: quill.root.innerHTML,
            }));
        });
    }, [quill]);

    useEffect(() => {
        if (!currentItem.is_dynamic_price) return;

        const combos = generateCombinations(currentItem.available_materials);

        setDynamicPrices((prev) =>
            combos.map((combo) => {
                const existing = prev.find(
                    (p) => JSON.stringify(p.materials) === JSON.stringify(combo)
                );

                return existing || { materials: combo, price: 0 };
            })
        );
    }, [currentItem.available_materials, currentItem.is_dynamic_price]);

    const loadCategories = async () => {
        try {
            const data = await GetCategories();
            setCategories(data);
        } catch {
            setMessage("Failed to load categories");
        }
    };

    const loadItems = async (categoryId: string) => {
        try {
            setLoading(true);
            const data = await GetItemsByCategory(categoryId);
            setItems(data);
        } catch {
            setMessage("Failed to load items");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteItem = async (id: string) => {
        try {
            await DelItem(id);
            setMessage("Item deleted");
            loadItems(selectedCategory);
        } catch {
            setMessage("Failed to delete item");
        }
    };

    const handleEditItem = (item: Item) => {

        setCurrentItem({ ...item });
        setSelectedCategory(item.category);
        setTagsInput(item.tags?.join(", ") || "");

        if (quill) {
            quill.root.innerHTML = item.text_description || "";
        }
    };

    const handleSaveItem = async () => {

        if (!currentItem.title || !currentItem.category) {
            setMessage("Title and category are required");
            return;
        }

        try {

            const itemToSave = {
                ...currentItem,
                tags: tagsInput.split(",").map(t => t.trim()),
                dynamic_prices: currentItem.is_dynamic_price ? dynamicPrices : [],
            };

            await AddItem(itemToSave as any);

            setMessage(itemToSave._id ? "Item updated" : "Item created");

            setCurrentItem(emptyItem);

            if (quill) {
                quill.setText("");
            }

            loadItems(selectedCategory);

        } catch {
            setMessage("Failed to save item");
        }
    };

    const resetForm = () => {
        setCurrentItem(emptyItem);
        setTagsInput("");

        if (quill) {
            quill.setText("");
        }
    };

    return (
        <div className="admin-items-page">

            <MenuAdmin />

            <h1 className="admin-items-title">Items Management</h1>

            {message && <div className="admin-items-message">{message}</div>}

            <div className="admin-items-form">

                <h2>{currentItem._id ? "Edit Item" : "Create Item"}</h2>

                <ImageUploader />

                <div className="admin-items-field">
                    <label>Title</label>
                    <input
                        value={currentItem.title}
                        onChange={(e) =>
                            setCurrentItem({ ...currentItem, title: e.target.value })
                        }
                    />
                </div>

                <div className="admin-items-field">
                    <label>Description</label>
                    <div style={{ height: "200px" }}>
                        <div ref={quillRef} />
                    </div>
                </div>

                <div className="admin-items-field">
                    <label>Category</label>

                    <select
                        value={currentItem.category}
                        onChange={(e) =>
                            setCurrentItem({
                                ...currentItem,
                                category: e.target.value,
                            })
                        }
                    >
                        <option value="">Select category</option>

                        {categories.map((cat) => (
                            <option key={cat._id} value={cat._id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="admin-items-field">
                    <label>Tags (comma separated)</label>

                    <input
                        value={tagsInput}
                        placeholder="modern, eco, bestseller"
                        onChange={(e) => setTagsInput(e.target.value)}
                    />
                </div>

                <div className="admin-items-row">

                    <div className="admin-items-field">
                        <label>Price</label>

                        <input
                            type="text"
                            value={formatPrice(currentItem.price)}
                            onChange={(e) => {
                                const raw = e.target.value.replace(/[^\d]/g, "");

                                setCurrentItem({
                                    ...currentItem,
                                    price: Number(raw),
                                });
                            }}
                        />

                    </div>

                    <div className="admin-items-field checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={currentItem.is_dynamic_price}
                                onChange={(e) =>
                                    setCurrentItem({
                                        ...currentItem,
                                        is_dynamic_price: e.target.checked,
                                    })
                                }
                            />
                            Dynamic Price
                        </label>
                    </div>

                    <div className="admin-items-field checkbox">
                        <label>
                            <input
                                type="checkbox"
                                checked={currentItem.is_to_order}
                                onChange={(e) =>
                                    setCurrentItem({
                                        ...currentItem,
                                        is_to_order: e.target.checked,
                                    })
                                }
                            />
                            Is To Order
                        </label>
                    </div>
                </div>

                {currentItem.is_dynamic_price && (
                    <div className="dynamic-pricing">

                        <h3>Dynamic Prices</h3>

                        {dynamicPrices.map((row, idx) => (

                            <div key={idx} className="dynamic-price-row">

                                <div className="materials">
                                    {row.materials.join(" / ")}
                                </div>

                                <input
                                    type="text"
                                    value={formatPriceDynamic(row.price)}
                                    onChange={(e) => {

                                        const raw = e.target.value.replace(/[^\d]/g, "");
                                        const updated = [...dynamicPrices];

                                        updated[idx].price = Number(raw);

                                        setDynamicPrices(updated);
                                    }}
                                />
                            </div>
                        ))}
                    </div>
                )}

                <div className="admin-items-field">
                    <label>Images (comma separated)</label>

                    <input
                        value={currentItem.images_link.join(", ")}
                        onChange={(e) =>
                            setCurrentItem({
                                ...currentItem,
                                images_link: e.target.value
                                    .split(",")
                                    .map((i) => i.trim()),
                            })
                        }
                    />
                </div>

                <div className="admin-items-field">
                    <label>Characteristics</label>

                    <input
                        value={currentItem.characteristics.join(",")}
                        onChange={(e) =>
                            setCurrentItem({
                                ...currentItem,
                                characteristics: e.target.value.split(","),
                            })
                        }
                    />
                </div>

                <div className="admin-items-field">
                    <label>Materials</label>

                    <input
                        value={
                            currentItem.available_materials &&
                            currentItem.available_materials
                                .map((g) => g.join(","))
                                .join(" | ")
                        }
                        onChange={(e) =>
                            setCurrentItem({
                                ...currentItem,
                                available_materials: e.target.value
                                    .split("|")
                                    .map((g) =>
                                        g.split(",").map((x) => x.trim())
                                    ),
                            })
                        }
                    />
                </div>

                <div className="admin-items-actions">

                    <button onClick={handleSaveItem}>
                        {currentItem._id ? "Update Item" : "Create Item"}
                    </button>

                    {currentItem._id && (
                        <button className="secondary" onClick={resetForm}>
                            Cancel Edit
                        </button>
                    )}
                </div>
            </div>

            <div className="admin-items-list">

                <h2>Items List</h2>

                <div className="admin-items-filter">

                    <label>Category</label>

                    <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                    >
                        <option value="">Select category</option>

                        {categories.map((cat) => (
                            <option key={cat._id} value={cat._id}>
                                {cat.name}
                            </option>
                        ))}
                    </select>
                </div>

                {loading && <div className="admin-items-loading">Loading...</div>}

                {!loading && items.length === 0 && (
                    <div className="admin-items-empty">No items</div>
                )}

                {items.map((item) => (

                    <div key={item._id} className="admin-items-card">

                        <div className="admin-items-info">

                            <div className="admin-items-name">
                                {item.title}
                            </div>

                            <div
                                className="admin-items-desc"
                                dangerouslySetInnerHTML={{
                                    __html: item.text_description,
                                }}
                            />
                        </div>

                        <div className="admin-items-card-actions">

                            <button onClick={() => handleEditItem(item)}>
                                Edit
                            </button>

                            <button
                                className="danger"
                                onClick={() => handleDeleteItem(item._id)}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

function generateCombinations(groups: string[][]): string[][] {

    if (!groups.length) return [];

    return groups.reduce<string[][]>(
        (acc, group) =>
            acc.flatMap((prev) => group.map((g) => [...prev, g])),
        [[]]
    );
}

const formatPriceDynamic = (value: number | string) => {
    const num = typeof value === "number" ? value : Number(value.replace(/\s/g, ""));
    if (!num) return "";
    return num.toLocaleString("ru-RU").replace(/,/g, " ");
};

const formatPrice = (value: number | undefined) => {
    if (!value) return "";
    return value.toLocaleString("ru-RU").replace(/,/g, " ");
};



export default AdminItems;
